import {
  documentSubtotal,
  documentTax,
  documentTotal,
  estimateWarrantyAfterSwap,
  feeStep,
  formatCents,
  formatMoney,
  framingNailerLabel,
  framingNailMismatches,
  linesAfterSwap,
  manufacturerProductFor,
  orderWarrantyAdvice,
  roofSquares,
  swapCost,
  tierLabel,
  MATERIAL_ORDER_BASE_STATUS,
  MATERIAL_ORDER_STEPS,
  jobAddress,
  missingColors,
  orderStatusLabel,
  paperworkHeader,
  quickMeasureCredit,
  supplierOrderEmail,
  toCents,
  placeMaterialOrderPatch,
  placingBlockedBy,
  PriceBook,
  quoteCheckEnabled,
  taxIsTyped,
  toDecimal,
  toggleChecklistStep,
  type Job,
  type JobFileEntry,
  type MaterialOrder,
  type PricedSupplier,
  type Product,
  type ProductPrice,
  type QuickMeasureCredit,
  type Reconciliation,
  sortSupplierDocuments,
  type RoofLayout,
  type Supplier,
  type MissingAccessory,
  type Cents,
  REGISTERED_TIER,
} from "@roof-hawk/core";
import { useCallback, useRef, useState } from "react";

import { ErrorNote, Modal, Row } from "../../components/ui";
import { EditHistoryProvider, UndoRedoButtons } from "../../lib/editHistory";
import { fmtDateTime } from "../../lib/format";
import { t, tn } from "../../i18n";
import { fetchFramingNails } from "../../lib/framingNails";
import { useAsync } from "../../lib/hooks";
import {
  materialOrderFilename,
  materialOrderPdfBase64,
  type MaterialOrderPdf,
} from "../../lib/materialOrderPdf";
import { printOrder } from "../../lib/printOrder";
import { CompanyCamPhotoPicker } from "./CompanyCamPhotoPicker";
import { ImportQuoteModal } from "./ImportQuoteModal";
import {
  QuoteCheckPanel,
  quotePlacementWarning,
  useQuoteCheck,
} from "./QuoteCheckPanel";
import { ReconcileModal, reconcileAgainstDocument } from "./ReconcileModal";
import {
  deleteRow,
  fetchCompanySettings,
  fetchJob,
  downloadJobFile,
  fetchOrderDocuments,
  fetchRoofLayouts,
  insertRow,
  roofReportBase64,
  signJobFile,
  updateRow,
  uploadOrderDocument,
} from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";
import { sendEmail } from "../../lib/gmail";
import { NumberCell, TextCell } from "../estimate/EstimateCard";
import { useGmailAccount } from "../email/useGmailAccount";
import { nextSort, OrderItemsTable } from "./WorkOrderEditor";

/** Every supplier's list prices, read fresh. */
async function readPriceBook(): Promise<PriceBook> {
  const [priceRows, supplierRows] = await Promise.all([
    supabase.from("product_prices").select("price_list_id,product_id,cost"),
    supabase.from("suppliers").select("id,price_list_id"),
  ]);
  return new PriceBook(
    unwrap<ProductPrice[]>(priceRows),
    unwrap<PricedSupplier[]>(supplierRows),
  );
}

/**
 * The material order editor — new in v1.
 *
 * Tax is a rate on the order, never a product attribute (ADR 0016), and
 * defaults to DEFAULT_MATERIAL_TAX_RATE for materials (10% since ADR 0248). The total feeds the estimate's Material Cost.
 */
export function MaterialOrderEditor({
  orderId,
  products,
  suppliers,
  onClose,
  onSaved,
}: {
  orderId: string;
  products: Product[];
  suppliers: Supplier[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { trades, profileId } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [placing, setPlacing] = useState(false);
  /*
   * Shown only when somebody tries to place the order.
   *
   * It used to sit under the checklist the whole time an order had a line
   * without a colour, which is most of an order's life — the shingle colour is
   * the customer's choice and often is not known when the order is generated.
   * A red panel that is always there is a red panel nobody reads. Jonathon,
   * 2026-08-13: "this really only needs to pop up if we try to place the
   * material order. that way we're prompted to fix it before we place."
   */
  const [colorsQueried, setColorsQueried] = useState(false);
  /** "Place order" pressed with no supplier chosen. See `placingBlockedBy`. */
  const [supplierQueried, setSupplierQueried] = useState(false);
  const [reconciliation, setReconciliation] = useState<{
    result: Reconciliation;
    filename: string;
    /** The `job_files.kind` of what was checked. */
    kind: string;
  } | null>(null);
  /** Whether the read-a-quote screen is open. */
  const [importing, setImporting] = useState(false);

  const orderQuery = useAsync(async () => {
    const { data, error: e } = await supabase
      .from("material_orders")
      .select("*, items:material_order_items(*)")
      .eq("id", orderId)
      .maybeSingle();
    if (e) throw new Error(e.message);
    return data as MaterialOrder | null;
  }, [orderId]);

  const order = orderQuery.data;

  /*
   * The job, for the two facts Lysa asked to see on the paperwork: whose house
   * this is and where it is. Read here as well as inside the send screen
   * because the printed copy needs them too, and a counter reading a printed
   * order is the case where getting the address wrong costs a delivery.
   */
  const printJob = useAsync(
    async () => (order ? await fetchJob(order.job_id) : null),
    [order?.job_id],
  );

  /** Re-read the order and push the new totals back to the estimate. */
  const reload = useCallback(async () => {
    await orderQuery.reload();
    await onSaved();
  }, [orderQuery, onSaved]);

  const guard = useCallback(
    async (action: () => Promise<void>) => {
      try {
        await action();
        await reload();
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [reload],
  );

  // The job's roof, so a line added by hand can work its own quantity out.
  // Above the guard below, because hooks cannot be called conditionally.
  // Null, not an empty roof, before the order has loaded: a check run against
  // "no measurements" reads every measured part as a difference.
  const layouts = useAsync(
    async () => (order ? await fetchRoofLayouts(order.job_id) : null),
    [order?.job_id],
  );

  // The supplier's receipts for this order. They are on the job's Docs card
  // too — this is the same rows, read by order, so the paperwork is beside the
  // order it settles rather than three screens away.
  const receipts = useAsync(
    async () => (order ? await fetchOrderDocuments(order.id) : []),
    [order?.id],
  );

  /*
   * `company_settings.quote_check_enabled` — whether the order is checked
   * against its quote at all. Off since 2026-09-21 (ADR 0190).
   *
   * `select *`, so a database the migration has not reached yet reads the
   * column as absent rather than refusing the whole row — and absent reads as
   * off, which is the answer that was wanted.
   */
  const company = useAsync(fetchCompanySettings, []);

  // The gun the job's crew nails with, so framing nails it cannot shoot are
  // pointed out below (ADR 0287). Re-read when the order is reopened, which
  // is how a crew changed on the work order since reaches it.
  const nailerQuery = useAsync(
    async () =>
      order
        ? (
            await fetchFramingNails({
              estimateId: order.estimate_id,
              trade: order.trade,
            })
          ).nailer
        : null,
    [order?.estimate_id, order?.trade],
  );

  // The supplier price book, for pricing the one swap that would reach the
  // next warranty tier (ADR 0309). Read once per order, for the figure the
  // hint shows; the swap itself reads it again at the moment it writes, as the
  // nail switch below does, so a line is never priced off the catalogue
  // default while this read is loading or has failed.
  const priceBook = useAsync(readPriceBook, [orderId]);

  // Checked against the estimate every time the order is open or changes —
  // nothing to press, because nothing needs supplying (ADR 0156). Unless the
  // check is switched off, in which case none of it runs.
  const quoteCheck = useQuoteCheck(
    order ?? null,
    layouts.loading || layouts.error ? null : layouts.data,
    layouts.error,
    quoteCheckEnabled(company.data?.quote_check_enabled),
  );

  // Only blank before the first load. Re-reading after an edit must not tear
  // the modal down and rebuild it — that is what made adding a line item look
  // like the screen refreshing.
  if (!order) return null;

  const items = (order.items ?? [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const patch = (values: Record<string, unknown>) =>
    guard(() => updateRow("material_orders", order.id, values));
  // Like `patch`, but a failure reaches the quote check, which then says the
  // review was not saved instead of reading as reviewed.
  const recordReview = async (values: Record<string, unknown>) => {
    await updateRow("material_orders", order.id, values);
    await reload();
  };

  const supplier = suppliers.find((s) => s.id === order.supplier_id);
  const supplierName = supplier?.name ?? null;

  // Lines that cannot go to the supplier yet. See `missingColors`.
  const blocking = missingColors(items, products);

  // Framing nails the crew's gun cannot shoot. Only before the order is
  // placed: after that the nails are bought, and a return is a person's call.
  const nailer = nailerQuery.data ?? null;
  const nailSwaps =
    order.status === MATERIAL_ORDER_BASE_STATUS
      ? framingNailMismatches(items, nailer, products)
      : [];
  /**
   * The line keeps its quantity; the nail, its name and its price change.
   *
   * Priced off the order's supplier's list, the way a generated order prices
   * the same swap, so the box costs the same whichever way it got there.
   */
  const switchNails = () =>
    guard(async () => {
      const [priceRows, supplierRows] = await Promise.all([
        supabase.from("product_prices").select("price_list_id,product_id,cost"),
        supabase.from("suppliers").select("id,price_list_id"),
      ]);
      const prices = new PriceBook(
        unwrap<ProductPrice[]>(priceRows),
        unwrap<PricedSupplier[]>(supplierRows),
      );
      for (const swap of nailSwaps) {
        await updateRow("material_order_items", swap.itemId, {
          product_id: swap.fit.id,
          name: swap.fit.name,
          unit: swap.fit.unit,
          unit_price: toDecimal(prices.costOf(swap.fit, order.supplier_id)),
        });
      }
    });

  /*
   * The manufacturer's warranty this order qualifies for, and the part or
   * two that would reach the next tier (ADR 0309). Only before the order is
   * placed: after that the parts are bought. The catalogue supplies each
   * line's category, which the line itself does not carry.
   */
  const categoryOf = new Map(products.map((p) => [p.id, p.category ?? null]));
  const warrantyLines = items.map((item) => ({
    ...item,
    category: item.product_id
      ? (categoryOf.get(item.product_id) ?? null)
      : null,
  }));
  const warranty =
    order.status === MATERIAL_ORDER_BASE_STATUS
      ? orderWarrantyAdvice(warrantyLines)
      : null;
  const squares = roofSquares(layouts.data ?? []);
  /**
   * The manufacturer's part for a missing category, priced for this supplier.
   * The cost is null until the supplier's list has been read: the catalogue
   * default is not what this supplier charges.
   */
  const partFor = (part: MissingAccessory) => {
    if (!warranty) return null;
    const product = manufacturerProductFor(
      warranty.manufacturer,
      part.part,
      products,
    );
    if (!product) return null;
    const cost = priceBook.data
      ? priceBook.data.costOf(product, order.supplier_id)
      : null;
    return { product, cost };
  };
  const warrantyFee = warranty
    ? feeStep(warranty.tier, warranty.target, squares)
    : null;

  /**
   * The line keeps its quantity; the part, its name and its price change.
   * Priced off the supplier's list read now, like the nail switch: a failed
   * read stops the swap and says so, rather than writing a default cost.
   *
   * A swap that lifts the order to a better tier moves the estimate's
   * warranty expense with it, ProShield's $5 to MasterShield's $7, because
   * that is what the manufacturer will be paid. Jonathon, 2026-10-27: "7. a".
   */
  const switchPart = (part: MissingAccessory) =>
    guard(async () => {
      const fit = partFor(part);
      const lineId = part.replaces?.id;
      if (!fit || !lineId) return;
      const prices = await readPriceBook();
      await updateRow("material_order_items", lineId, {
        product_id: fit.product.id,
        name: fit.product.name,
        unit: fit.product.unit,
        unit_price: toDecimal(prices.costOf(fit.product, order.supplier_id)),
      });
      if (!order.estimate_id) return;
      const reached =
        orderWarrantyAdvice(linesAfterSwap(warrantyLines, lineId, fit.product))
          ?.tier ?? null;
      const estimate = unwrap<{ warranty: string | null }[]>(
        await supabase
          .from("estimates")
          .select("warranty")
          .eq("id", order.estimate_id),
      );
      const next = estimateWarrantyAfterSwap(estimate[0]?.warranty, reached);
      if (next)
        await updateRow("estimates", order.estimate_id, { warranty: next });
    });

  /**
   * Write the lines a person accepted off a supplier's quotation.
   *
   * `name` rather than `description`: this table has the one and not the other
   * — work orders are the other way round — and the supplier's own wording is
   * what goes in it, because that is what their invoice will say.
   */
  const addQuotedLines = (
    rows: readonly {
      product_id: string | null;
      name: string;
      qty: number;
      unit: string | null;
      unit_price: number;
    }[],
  ) =>
    guard(async () => {
      let sort = nextSort(items);
      for (const row of rows) {
        await insertRow("material_order_items", {
          material_order_id: order.id,
          product_id: row.product_id,
          name: row.name,
          qty: row.qty,
          unit: row.unit,
          unit_price: row.unit_price,
          sort_order: sort,
        });
        sort += 10;
      }
      await reload();
    });

  /** Duplicate the order and its lines onto the same job, as a fresh draft. */
  const copyOrder = () =>
    guard(async () => {
      const copy = await insertRow<{ id: string }>("material_orders", {
        job_id: order.job_id,
        estimate_id: order.estimate_id,
        supplier_id: order.supplier_id,
        supplier_location: order.supplier_location,
        trade: order.trade,
        delivery_note: order.delivery_note,
        pickup: order.pickup,
        tax_rate: order.tax_rate,
        status: "not_placed",
      });
      for (const item of items) {
        await insertRow("material_order_items", {
          material_order_id: copy.id,
          product_id: item.product_id,
          // `name`, not `description`: see `addQuotedLines` above. Writing
          // `description` here copied every line without the thing that says
          // what it is, which on a line with no product is the only thing that
          // did.
          name: item.name,
          color: item.color,
          qty: item.qty,
          unit: item.unit,
          unit_price: item.unit_price,
          sort_order: item.sort_order,
        });
      }
    });

  /** Delete the whole order, once somebody has said so out loud. */
  const deleteOrder = () => {
    if (
      !window.confirm(
        t("Delete Material Order MO-{n}?", { n: order.order_number }) +
          "\n\n" +
          t("Its lines go with it. This cannot be undone."),
      )
    )
      return;
    void (async () => {
      await deleteRow("material_orders", order.id);
      await onSaved();
      onClose();
    })();
  };

  return (
    <Modal
      title={t("Material Order MO-{n}", { n: order.order_number })}
      subtitle={t(
        "Materials for this job. Totals feed the estimate's Material Cost line.",
      )}
      onClose={onClose}
      wide
      actions={
        <>
          {/* Red, in the action row, beside Done. Jonathon, 2026-08-27: the
              delete has to be somewhere it can be found — it was a grey line of
              text at the foot of a long editor, under everything, which is
              where you look last. It still asks first: an order deleted by
              accident takes its lines with it. */}
          <button className="btn-danger" onClick={deleteOrder}>
            {t("Delete")}
          </button>
          <button className="btn-primary" onClick={onClose}>
            {t("Done")}
          </button>
        </>
      }
    >
      {importing && (
        <ImportQuoteModal
          products={products}
          onClose={() => setImporting(false)}
          onAdd={addQuotedLines}
        />
      )}

      {/* Everything inside can be undone — see lib/editHistory.tsx. */}
      <EditHistoryProvider onChanged={reload}>
        <ErrorNote error={error} />
        <UndoRedoButtons />

        {nailSwaps.length > 0 && nailer !== null && (
          <div className="config-warn" role="alert">
            {t("The crew on this job uses a {nailer} framing nail gun.", {
              nailer: t(framingNailerLabel(nailer)),
            })}{" "}
            {nailSwaps
              .map((swap) => `${swap.current.name} → ${swap.fit.name}`)
              .join("; ")}
            .{" "}
            <button className="btn-small" onClick={() => void switchNails()}>
              {t("Switch to {nailer} nails", {
                nailer: t(framingNailerLabel(nailer)),
              })}
            </button>
          </div>
        )}

        {warranty && warranty.short > 0 && (
          <div
            className="config-warn"
            role="alert"
            data-testid="warranty-nudge"
          >
            <b>
              {t("{present} of {total} {manufacturer} parts on this order", {
                present: warranty.present.length,
                total: warranty.present.length + warranty.missing.length,
                manufacturer: warranty.manufacturer,
              })}
            </b>
            {" — "}
            {warranty.tier
              ? t("it qualifies for {tier}", { tier: tierLabel(warranty.tier) })
              : t("not enough for an enhanced warranty")}
            .{" "}
            {tn(
              warranty.short,
              "{tier} needs one more",
              "{tier} needs {n} more",
              { tier: tierLabel(warranty.target) },
            )}
            {warrantyFee !== null && warrantyFee > 0 && (
              <>
                {" "}
                {t(
                  "and the registration fee goes up {fee} ({to}/sq instead of {from}/sq on {squares} squares)",
                  {
                    fee: formatMoney(warrantyFee),
                    to: formatMoney(
                      REGISTERED_TIER[warranty.target].perSquare ??
                        (0 as Cents),
                    ),
                    from: formatMoney(
                      (warranty.tier &&
                        REGISTERED_TIER[warranty.tier].perSquare) ??
                        (0 as Cents),
                    ),
                    squares: squares.toFixed(2),
                  },
                )}
              </>
            )}
            :
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {warranty.toTarget.map((part) => {
                const fit = partFor(part);
                const extra =
                  fit && fit.cost !== null && part.replaces
                    ? swapCost(part.replaces, fit.cost)
                    : null;
                return (
                  <li key={part.key}>
                    {part.replaces
                      ? `${part.replaces.name} → ${fit?.product.name ?? part.name}`
                      : t("add {name}", {
                          name: fit?.product.name ?? part.name,
                        })}
                    {extra !== null &&
                      ` (${t("{amount} in material", {
                        amount: `${extra >= 0 ? "+" : "−"}${formatMoney(Math.abs(extra) as Cents)}`,
                      })})`}
                    {part.replaces && fit && (
                      <>
                        {" "}
                        <button
                          className="btn-small"
                          onClick={() => void switchPart(part)}
                        >
                          {t("Swap it")}
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            {priceBook.error && (
              <ErrorNote
                error={t(
                  "Could not read the supplier's prices, so the material figure is not shown: {error}",
                  { error: priceBook.error },
                )}
              />
            )}
          </div>
        )}

        <QuoteCheckPanel
          order={order}
          check={quoteCheck}
          onReviewed={recordReview}
        />

        <OrderItemsTable
          measurements={layouts.data ?? null}
          items={items}
          products={products}
          showColor
          showName
          table="material_order_items"
          newRow={() => ({
            material_order_id: order.id,
            qty: 1,
            unit: "EA",
            unit_price: 0,
            sort_order: nextSort(items),
          })}
        />

        <div style={{ marginTop: 16 }}>
          <div className="totrow">
            <span className="lbl">{t("Subtotal")}</span>
            <span className="num">{formatCents(documentSubtotal(order))}</span>
            <span className="num" />
            <span className="num" />
          </div>
          <div className="totrow">
            {/* The rate sits in the label column, not a trailing one: the
              narrow-screen rules hide columns 3 and 4, and this is the only
              place in the app sales tax is entered. */}
            <span className="lbl taxrate">
              {t("Sales Tax")}
              <NumberCell
                value={order.tax_rate}
                width={58}
                variant="plain"
                // A new rate is a decision to go back to the rate, so it
                // clears a typed amount rather than being silently ignored.
                onSave={(v) => void patch({ tax_rate: v, tax_amount: null })}
              />
              %
              {/* Typed off the supplier's bill (ADR 0243). Said beside the
                  rate, which no longer decides, with the way back. */}
              {taxIsTyped(order) && (
                <span className="na-hint">
                  {t("typed")} ·{" "}
                  <span
                    className="act"
                    role="button"
                    onClick={() => void patch({ tax_amount: null })}
                  >
                    {t("use the rate")}
                  </span>
                </span>
              )}
            </span>
            <span className="num">
              {/* Jonathon, 2026-09-30: "we need to be able to manually edit
                  tax on material orders when needed". Clicking in and out
                  without changing it must not turn the worked-out figure
                  into a typed one, so an unchanged amount is not saved.
                  An emptied box means "use the rate", not a typed $0: $0
                  is typed as 0. */}
              <NumberCell
                value={toDecimal(documentTax(order))}
                width={90}
                onSave={(v) => {
                  if (!taxIsTyped(order) && toCents(v) === documentTax(order))
                    return;
                  void patch({ tax_amount: v });
                }}
                onClear={() => {
                  if (taxIsTyped(order)) void patch({ tax_amount: null });
                }}
              />
            </span>
            <span className="num" />
            <span className="num" />
          </div>
          <div className="totrow strong">
            <span className="lbl">
              <b>{t("Total")}</b>
            </span>
            <span className="num">
              <b>{formatCents(documentTotal(order))}</b>
            </span>
            <span className="num" />
            <span className="num" />
          </div>
        </div>

        {/* Below the line items on purpose. Once a job has been
          sent, what somebody opens this to read is the work, not
          the crew and the dates — Jonathon, 2026-08-08. */}
        <div className="cprev">
          <div>
            <Row label={t("Supplier")}>
              <select
                value={order.supplier_id ?? ""}
                onChange={(e) =>
                  void patch({ supplier_id: e.target.value || null })
                }
              >
                <option value="">—</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </Row>
            <Row label={t("Trade")}>
              <select
                value={order.trade ?? ""}
                onChange={(e) => void patch({ trade: e.target.value || null })}
              >
                <option value="">—</option>
                {trades.map((trade) => (
                  <option key={trade.id} value={trade.name}>
                    {trade.name}
                  </option>
                ))}
              </select>
            </Row>
            {/*
              Read, not chosen. Every status this order can hold is one of the
              rungs on the checklist, so the dropdown offered nothing the ticks
              did not — except the chance to disagree with them, and an order
              reading "ordered" with nothing ticked is a lie the board believes.
              Jonathon, 2026-09-12: the checklist drives it.
            */}
            <Row label={t("Status")}>
              <span>{t(orderStatusLabel(order.status))}</span>
            </Row>
            <Row label={t("Delivery Date")}>
              <input
                type="date"
                value={order.delivery_date ?? ""}
                onChange={(e) =>
                  void patch({ delivery_date: e.target.value || null })
                }
              />
            </Row>
            <Row label={t("Delivery Note")}>
              {/* Where to put the load, and what the driver needs to know to
                  get it there. More than a slot's worth, every time. */}
              <TextCell
                multiline
                value={order.delivery_note}
                onSave={(v) => void patch({ delivery_note: v || null })}
              />
            </Row>
            <Row label={t("Pickup")}>
              <button
                className={`tog${order.pickup ? " on" : ""}`}
                onClick={() => void patch({ pickup: !order.pickup })}
              />
            </Row>
            <Row label={t("Hold Order")}>
              <button
                className={`tog${order.hold_order ? " on" : ""}`}
                onClick={() => void patch({ hold_order: !order.hold_order })}
              />
            </Row>
          </div>

          <div>
            <Row label={t("Change Order")}>
              <button
                className={`tog${order.is_change_order ? " on" : ""}`}
                onClick={() =>
                  void patch({ is_change_order: !order.is_change_order })
                }
              />
            </Row>

            {/*
              One list, in the order the order happens.
              --------------------------------------------------------------
              Lysa, 2026-09-12, on this screen: "I think this page creates some
              confusion." It had a heading called Checklist above a button, the
              checklist itself three sections lower with Supplier Paperwork
              wedged into the middle of it, and two controls a word apart —
              "Order placed" the button and "Order Placed" the row, one an
              action and one a state.

              So there is one list and one place to act. Each rung says what
              happened and when; placing the order goes through the screen that
              sends it, and the row underneath says where it went. Every other
              button is below, under Actions, named for who the copy is for.
            */}
            <div className="chk-sub">{t("This Order")}</div>
            {(
              [
                ["ordered_at", "Order placed", "Place order"],
                [
                  "picked_up_at",
                  // One word, one meaning. "Pickup" on the left of this screen
                  // is how the material comes; this rung is whether it has
                  // arrived, and calling both of them Pickup is what made the
                  // pair unreadable.
                  order.pickup ? "Picked up" : "Delivered",
                  order.pickup ? "Mark picked up" : "Mark delivered",
                ],
                ["paid_at", "Paid", "Mark paid"],
              ] as const
            ).map(([column, label, action]) => {
              const on = Boolean(order[column]);
              return (
                <div key={column}>
                  <div className={`ck${on ? " on" : ""}`}>
                    <span className="dotc">
                      <svg viewBox="0 0 24 24" fill="none" strokeWidth="3">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                    <span>{t(label)}</span>
                    {on && (
                      <span className="when">{fmtDateTime(order[column])}</span>
                    )}
                    {/* Sending it again is its own word, because it is its own
                        act: an order that has changed since it went needs the
                        supplier to hear so, and un-ticking the rung is not how
                        anybody would ask for that. */}
                    {column === "ordered_at" && on && (
                      <span className="act" onClick={() => setPlacing(true)}>
                        {t("Send again")}
                      </span>
                    )}
                    <span
                      className="act"
                      onClick={() => {
                        /*
                         * Placing the order is the one rung that is somebody
                         * else being told something, so it goes through the
                         * screen that tells them. Ticking it without an email
                         * is still possible — "Mark as placed" is a button on
                         * that screen — but it is now a thing you choose
                         * rather than the only thing this row could do.
                         */
                        if (column === "ordered_at" && !on) {
                          if (placingBlockedBy(order)) {
                            setSupplierQueried(true);
                            return;
                          }
                          if (blocking.length > 0) {
                            setColorsQueried(true);
                            return;
                          }
                          setPlacing(true);
                          return;
                        }
                        void patch(
                          toggleChecklistStep(
                            MATERIAL_ORDER_STEPS,
                            order,
                            column,
                            MATERIAL_ORDER_BASE_STATUS,
                            new Date().toISOString(),
                          ),
                        );
                      }}
                    >
                      {on ? t("Undo") : t(action)}
                    </span>
                  </div>
                  {/* Under the rung it belongs to, rather than as a line of
                      text somewhere else on the screen: whether an order was
                      emailed is a fact about it being placed. */}
                  {column === "ordered_at" && on && (
                    <div className="na-hint" style={{ marginLeft: 26 }}>
                      {order.supplier_emailed_at
                        ? t("Emailed to {to} on {when}.", {
                            to:
                              order.supplier_emailed_to ??
                              supplierName ??
                              t("the supplier"),
                            when: fmtDateTime(order.supplier_emailed_at),
                          })
                        : t(
                            "Marked placed by hand — no email was sent from here.",
                          )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* A colour missing on the shingles is not an order, it is a phone
              call two days later when the delivery is already late. Shown when
              somebody tries to place the order, never before: it was on screen
              for most of an order's life, and a red panel that is always there
              is a red panel nobody reads. */}
            {supplierQueried &&
              !order.ordered_at &&
              placingBlockedBy(order) !== null && (
                <div className="blockbox" role="alert">
                  <b>{t(placingBlockedBy(order) as string)}</b>{" "}
                  {t("It is under Supplier, above.")}
                </div>
              )}
            {colorsQueried && blocking.length > 0 && !order.ordered_at && (
              <div className="blockbox">
                <b>
                  {tn(
                    blocking.length,
                    "Fill these in before the order goes: 1 line still need a color",
                    "Fill these in before the order goes: {n} lines still need a color",
                  )}
                </b>
                <ul>
                  {blocking.map((b) => (
                    <li key={b.itemId}>
                      {b.description}
                      {b.options.length > 0 && (
                        <span className="na-hint">
                          {" "}
                          — {b.options.join(` ${t("or")} `)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="chk-sub">{t("Actions")}</div>
            <div className="wo-actions">
              {/* Named for who each copy is for. "Print PDF" and "Print PDF No
                  Pricing" said what the button does to a file, not which of the
                  two you want in your hand. */}
              <button
                className="addrow ghost"
                onClick={() =>
                  printMaterialOrder(
                    order,
                    items,
                    supplierName,
                    printJob.data ?? null,
                    true,
                  )
                }
              >
                {t("Print For The Supplier")}
              </button>
              <button
                className="addrow ghost"
                onClick={() =>
                  printMaterialOrder(
                    order,
                    items,
                    supplierName,
                    printJob.data ?? null,
                    false,
                  )
                }
              >
                {t("Print Without Prices")}
              </button>
              {/* It makes a second order, which is not what "Copy" says next
                  to two buttons that print one. */}
              <button className="addrow ghost" onClick={() => void copyOrder()}>
                {t("Duplicate This Order")}
              </button>
              {/* The lines come off the supplier's own quotation rather than
                  being typed in twice. Nothing is written until a person has
                  read what it found — see ImportQuoteModal. */}
              <button
                className="addrow ghost"
                onClick={() => setImporting(true)}
              >
                {t("Read A Supplier Quote")}
              </button>
              <AcknowledgementUpload
                items={items}
                taxRate={order.tax_rate}
                jobId={order.job_id}
                orderId={order.id}
                orderNumber={order.order_number}
                profileId={profileId ?? null}
                onFiled={() => void receipts.reload()}
                onReconciled={(result, filename) => {
                  // What this button files: a receipt or an invoice.
                  setReconciliation({
                    result,
                    filename,
                    kind: "supplier_receipt",
                  });
                  void patch({
                    document_filename: filename,
                    reconciled_at: new Date().toISOString(),
                  });
                }}
              />
            </div>
            {order.reconciled_at && (
              <div className="na-hint">
                {order.document_filename
                  ? t("Last checked {when} against {file}", {
                      when: fmtDateTime(order.reconciled_at),
                      file: order.document_filename,
                    })
                  : t("Last checked {when}", {
                      when: fmtDateTime(order.reconciled_at),
                    })}
              </div>
            )}
            <OrderReceipts
              files={receipts.data ?? []}
              items={items}
              taxRate={order.tax_rate}
              orderId={order.id}
              onReconciled={(result, filename, kind) => {
                setReconciliation({ result, filename, kind });
                void patch({
                  document_filename: filename,
                  reconciled_at: new Date().toISOString(),
                });
              }}
            />
            <div className="soonlist">
              <span>{t("Save PDF To Docs")}</span>
              <span>{t("Update Supplier Pricing")}</span>
              <span>{t("Pull Latest Pricing")}</span>
              <span>{t("Push To QuickBooks")}</span>
            </div>
            <div className="na-hint">
              {t(
                "Greyed items need something that is not built yet — see the note below.",
              )}
            </div>
          </div>
        </div>

        {reconciliation && (
          <ReconcileModal
            order={order}
            supplierName={supplierName}
            supplierEmail={supplier?.email ?? null}
            documentName={reconciliation.filename}
            documentKind={reconciliation.kind}
            reconciliation={reconciliation.result}
            onClose={() => setReconciliation(null)}
            onChanged={() => orderQuery.reload()}
          />
        )}

        {placing && (
          <PlaceOrderModal
            order={order}
            items={items}
            supplier={supplier ?? null}
            layouts={layouts.data ?? []}
            quoteWarning={quotePlacementWarning(quoteCheck)}
            onClose={() => setPlacing(false)}
            onPlaced={async (creditAsked, emailedTo) => {
              // Also the screen behind "Send again": an order already placed
              // keeps its ticks, its status and the day it was placed.
              const changes = placeMaterialOrderPatch(order, {
                now: new Date().toISOString(),
                emailedTo,
                creditAsked,
              });
              if (Object.keys(changes).length > 0) await patch(changes);
              setPlacing(false);
            }}
          />
        )}
      </EditHistoryProvider>
    </Modal>
  );
}

/**
 * Reading the supplier's receipt, acknowledgement or invoice.
 *
 * Same reader as a credit memo — the documents are the same shape — and the
 * same refusal to guess. A scan with no text layer produces nothing, which is
 * the honest answer rather than a reconciliation built on air.
 *
 * The file is kept whatever the reader makes of it, and kept first. A receipt
 * that cannot be read is still the receipt: it is the proof of what the
 * supplier charged, and the case where the reader fails is exactly the case
 * where somebody will want to open the document by eye later. Filing it is
 * also the write that must not be lost to a reader that throws.
 */
function AcknowledgementUpload({
  items,
  taxRate,
  jobId,
  orderId,
  orderNumber,
  profileId,
  onFiled,
  onReconciled,
}: {
  items: readonly {
    id: string;
    product_id: string | null;
    name: string | null;
    qty: unknown;
    unit_price: unknown;
  }[];
  taxRate: unknown;
  jobId: string;
  orderId: string;
  orderNumber: number;
  profileId: string | null;
  onFiled: () => void;
  onReconciled: (result: Reconciliation, filename: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  // On a phone there is no CompanyCam browser extension to add the job's
  // photos to the file picker, so Roof Board lists them itself.
  const [picking, setPicking] = useState(false);

  async function handle(file: File) {
    setBusy(true);
    try {
      let filed = true;
      try {
        await uploadOrderDocument({
          jobId,
          materialOrderId: orderId,
          orderNumber,
          file,
          profileId,
        });
        onFiled();
      } catch (e) {
        // Say so rather than failing silently: somebody who thinks the receipt
        // is filed will not keep the email it came in.
        filed = false;
        window.alert(
          t("{file} could not be saved to Docs.", { file: file.name }) +
            "\n\n" +
            `${e instanceof Error ? e.message : String(e)}\n\n` +
            t("The order was still checked against it."),
        );
      }

      const kept = filed
        ? ` ${t("The file itself is on Docs and on this order.")}`
        : "";
      let result: Reconciliation | null = null;
      try {
        result = await reconcileAgainstDocument(file, items, taxRate, orderId);
      } catch (e) {
        // The reader says why in its own words — "the reader is busy", "not
        // configured" — and any of those is more use than a silent nothing.
        window.alert(
          t("{file} could not be read.", { file: file.name }) +
            "\n\n" +
            `${e instanceof Error ? e.message : String(e)}\n\n` +
            `${t("Check the order by eye.")}${kept}`,
        );
        return;
      }

      if (!result) {
        window.alert(
          t("Nothing could be read from {file}.", { file: file.name }) +
            "\n\n" +
            `${t("It may not be a receipt at all. Check the order by eye.")}${kept}`,
        );
        return;
      }
      onReconciled(result, file.name);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        // A photograph of a counter receipt as readily as the PDF a supplier
        // emails — the reader behind it can look at either.
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handle(file);
        }}
      />
      <button
        className="addrow ghost"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? t("Reading…") : t("Upload Receipt / Invoice")}
      </button>
      <button
        className="addrow ghost"
        disabled={busy}
        onClick={() => setPicking(true)}
      >
        {t("Receipt From CompanyCam")}
      </button>
      {picking && (
        <CompanyCamPhotoPicker
          jobId={jobId}
          onPick={(file) => void handle(file)}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}

/**
 * The documents filed against this order.
 *
 * The same rows the job's Docs card lists — one register, read two ways
 * (ADR 0077). Here they are beside the order they settle, which is where
 * somebody asking "what did we actually pay for this?" is standing.
 *
 * Sorted the way the supplier sends them — acknowledgement, invoice, credit
 * memo — newest first within each (ADR 0311). An invoice that arrived by
 * email to orders@ was filed without anybody reading it against the order,
 * so "Check against the order" runs the same check an upload gets
 * (ADR 0145), from the filed file itself.
 */
function OrderReceipts({
  files,
  items,
  taxRate,
  orderId,
  onReconciled,
}: {
  files: readonly JobFileEntry[];
  items: readonly OrderLineForCheck[];
  taxRate: unknown;
  orderId: string;
  onReconciled: (
    result: Reconciliation,
    filename: string,
    kind: string,
  ) => void;
}) {
  const [checking, setChecking] = useState<string | null>(null);
  if (files.length === 0) return null;

  async function open(entry: JobFileEntry) {
    // Signed on demand: the bucket is private, and a link goes stale.
    window.open(await signJobFile(entry), "_blank", "noopener");
  }

  async function check(entry: JobFileEntry) {
    setChecking(entry.id);
    try {
      const file = await downloadJobFile(entry);
      const result = await reconcileAgainstDocument(
        file,
        items,
        taxRate,
        orderId,
      );
      if (!result) {
        window.alert(
          t("Nothing could be read from {file}.", { file: entry.title }) +
            "\n\n" +
            t("Check the order by eye."),
        );
        return;
      }
      onReconciled(result, entry.title, entry.kind);
    } catch (e) {
      window.alert(
        t("{file} could not be read.", { file: entry.title }) +
          "\n\n" +
          `${e instanceof Error ? e.message : String(e)}\n\n` +
          t("Check the order by eye."),
      );
    } finally {
      setChecking(null);
    }
  }

  const checkable = (entry: JobFileEntry) =>
    entry.kind === "supplier_invoice" ||
    entry.kind === "supplier_receipt" ||
    entry.kind === "supplier_credit_memo";

  return (
    <div className="orderdocs">
      <div className="na-hint">
        {t("Filed on this order and on the job’s Docs card:")}
      </div>
      {sortSupplierDocuments(files).map((entry) => (
        <div className="orderdoc" key={entry.id}>
          <button className="linkish" onClick={() => void open(entry)}>
            {entry.title}
          </button>
          <span className="sub">{fmtDateTime(entry.created_at)}</span>
          {checkable(entry) && (
            <button
              className="linkish"
              disabled={checking !== null}
              onClick={() => void check(entry)}
            >
              {checking === entry.id
                ? t("Checking…")
                : t("Check against the order")}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** An order line, as far as the check reads it. */
type OrderLineForCheck = Parameters<typeof reconcileAgainstDocument>[1][number];

/**
 * Place Material Order.
 *
 * There is no supplier integration (ADR 0093), so placing an order is a person
 * putting the list in front of another person. Three ways out of this screen,
 * and they are all the same list: email it from the rep's own mailbox, copy it
 * for a text or a phone call, or just record that it went out because it was
 * placed at the counter.
 *
 * **The email carries three things the clipboard cannot.** The order goes as a
 * PDF with prices, which is what a counter prints and walks to the racks; the
 * roof's measurement report hangs off it, so they can check the quantities
 * against the roof they are pricing; and when the supplier is one that credits
 * a QuickMeasure report — Heely-Brown, so far — both the letter and the PDF ask
 * for that credit, naming the address and the date the report was ordered
 * (ADR 0110). A rep who forgets to ask has bought a report that should have
 * been free, so the asking is written for them rather than left to remember.
 */
function PlaceOrderModal({
  order,
  items,
  supplier,
  layouts,
  quoteWarning = null,
  onClose,
  onPlaced,
}: {
  order: MaterialOrder;
  items: NonNullable<MaterialOrder["items"]>;
  supplier: Supplier | null;
  /** The job's roofs, for the report that goes with the order. */
  layouts: readonly RoofLayout[];
  /**
   * Differences from the quote nobody has reviewed. Said, never a reason to
   * stop: a person can act outside the estimate (ADR 0156).
   */
  quoteWarning?: string | null;
  onClose: () => void;
  /**
   * Records the order as placed. `creditAsked` also stamps the credit claim,
   * and `emailedTo` is the address it went to — absent when somebody pressed
   * "Mark as placed" for an order given over a counter or a phone.
   */
  onPlaced: (creditAsked: boolean, emailedTo?: string) => Promise<void>;
}) {
  const { account } = useGmailAccount();
  const supplierName = supplier?.name ?? null;

  /** The job, and whether this job's credit has been claimed already. */
  const context = useAsync(async () => {
    const [job, siblings] = await Promise.all([
      fetchJob(order.job_id),
      supabase
        .from("material_orders")
        .select("id,quickmeasure_credit_requested_at")
        .eq("job_id", order.job_id),
    ]);
    if (siblings.error) throw new Error(siblings.error.message);
    return {
      job,
      orders: (siblings.data ?? []) as {
        id: string;
        quickmeasure_credit_requested_at: string | null;
      }[],
    };
  }, [order.job_id]);

  const job = context.data?.job ?? null;

  /**
   * The report that goes with the order — the newest roof that has one.
   *
   * One, not all of them: a job with a house and a detached garage has two
   * reports and a supplier does not want both stapled to an order for shingles.
   * The rep can turn it off.
   */
  const report = layouts
    .filter((layout) => layout.pdf_path)
    .slice()
    .sort((a, b) => (a.captured_at < b.captured_at ? 1 : -1))[0];

  const credit = job
    ? quickMeasureCredit({
        job,
        supplier,
        order,
        history: { orders: context.data?.orders ?? [] },
      })
    : null;

  const [attach, setAttach] = useState(true);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  /** The letter as this screen wrote it, to tell an edit from a rewrite. */
  const [composed, setComposed] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const letter = job
    ? supplierOrderEmail({
        order,
        items,
        supplierName,
        job,
        credit: credit ?? undefined,
        attachmentName: attach && report ? reportName(report) : null,
        orderPdfName: materialOrderFilename(`MO-${order.order_number}`),
      })
    : null;

  /*
   * Written the first time the job loads, and re-written afterwards only while
   * the rep has not touched it — unticking the report has to take "the
   * measurement report is attached" back out, and a body that argues with the
   * attachment is how a supplier ends up waiting for a file that never came.
   * Once somebody has typed in it, it is theirs.
   */
  if (
    letter &&
    (composed === null || (text === composed && text !== letter.body))
  ) {
    if (composed === null) {
      setTo(supplier?.email ?? "");
      setSubject(letter.subject);
    }
    setText(letter.body);
    setComposed(letter.body);
  }

  // The editor stops "Place order" first; this holds for any other way in.
  const blocked = placingBlockedBy(order);

  /** Send it, mark the order placed, and stamp the credit claim if it asked. */
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      /*
       * The order as a file, always: a counter prints it and walks it to the
       * racks, which is what "email the order" has to mean. The measurement
       * report goes with it when the job has one and the rep has left it
       * ticked.
       */
      const number = `MO-${order.order_number}`;
      const attachments = [
        {
          filename: materialOrderFilename(number),
          mimeType: "application/pdf",
          content: await materialOrderPdfBase64(
            orderPdf({ order, items, supplierName, job, credit }),
          ),
        },
      ];
      if (attach && report?.pdf_path) {
        attachments.push({
          filename: reportName(report),
          mimeType: "application/pdf",
          content: await roofReportBase64(report.pdf_path),
        });
      }
      await sendEmail({
        to: to.trim(),
        subject: subject.trim(),
        text,
        jobId: order.job_id,
        // A supplier is not the customer: this must not tick the follow-up
        // clock that counts contact with the homeowner.
        internal: true,
        attachments,
      });
      await onPlaced(Boolean(credit?.claim), to.trim());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t("Place Material Order")}
      subtitle={t(
        "Email the order to the supplier as a PDF, or send it your own way and record that it went out.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-ghost"
            onClick={() => {
              void navigator.clipboard?.writeText(text);
              setCopied(true);
            }}
          >
            {copied ? t("Copied!") : t("Copy order")}
          </button>
          <button
            className="btn-ghost"
            disabled={busy || blocked !== null}
            onClick={() => void onPlaced(false)}
          >
            {t("Mark as placed")}
          </button>
          <button
            className="btn-primary"
            disabled={
              busy || blocked !== null || !account || !to.trim() || !job
            }
            onClick={() => void send()}
          >
            {busy ? t("Sending…") : t("Email & mark placed")}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />

      {blocked && (
        <div className="config-warn" role="alert">
          {t(blocked)}
        </div>
      )}

      {/* First, because it is the last chance to catch it before the supplier
          reads the order. A warning only: both buttons still work. */}
      {quoteWarning && (
        <div className="config-warn" role="alert">
          {quoteWarning}
        </div>
      )}

      {!account && (
        <div className="config-warn">
          {t(
            "Your Google account is not connected, so this cannot be emailed from here. Copy the order and send it your own way, then Mark as placed.",
          )}
        </div>
      )}

      {/* Why the credit is not being asked for, when it might have been. Said
          out loud: a missing paragraph and a missing credit look identical. */}
      {credit?.why && <div className="config-warn">{credit.why}</div>}

      {credit?.claim && (
        <div className="config-warn" role="status">
          {t(
            "This email asks {supplier} to credit the QuickMeasure report for this roof. It is asked once per job — send it and it will not be asked again.",
            { supplier: supplierName ?? "" },
          )}
        </div>
      )}

      <div className="fgrid one">
        <div className="field">
          <label>{t("To")}</label>
          <input
            type="email"
            aria-label={t("To")}
            value={to}
            placeholder={
              supplierName
                ? t(
                    "No order email for {supplier} — add one in Tools → Suppliers",
                    { supplier: supplierName },
                  )
                : t("Supplier email")
            }
            onChange={(e) => setTo(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>{t("Subject")}</label>
          <input
            aria-label={t("Subject")}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>{t("Attached")}</label>
          <span className="na-hint">
            {t("{file} — the order itself, with prices", {
              file: materialOrderFilename(`MO-${order.order_number}`),
            })}
          </span>
        </div>
        <div className="field">
          <label>{t("Measurement report")}</label>
          {report ? (
            <span>
              <input
                type="checkbox"
                aria-label={t("Attach the measurement report")}
                checked={attach}
                onChange={(e) => setAttach(e.target.checked)}
                disabled={busy}
              />{" "}
              {t("Attach {file}", { file: reportName(report) })}
            </span>
          ) : (
            <span className="na-hint">
              {t(
                "None on this job. Upload one on Roof Measurements and it goes with the order.",
              )}
            </span>
          )}
        </div>
        <div className="field">
          <label>{t("Order")}</label>
          <textarea
            aria-label={t("Order")}
            rows={12}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * The order, as the PDF wants it.
 *
 * Every figure is worked out by the core's document helpers — the same ones the
 * screen and the ladder read — so the file, the screen and the estimate cannot
 * disagree about what this order costs.
 */
function orderPdf({
  order,
  items,
  supplierName,
  job,
  credit,
}: {
  order: MaterialOrder;
  items: NonNullable<MaterialOrder["items"]>;
  supplierName: string | null;
  job: Job | null;
  credit: QuickMeasureCredit | null;
}): MaterialOrderPdf {
  return {
    number: `MO-${order.order_number}`,
    supplierName,
    jobNumber: job?.job_number ?? null,
    jobAddress: job ? jobAddress(job) : "",
    customerName: job?.contact?.full_name ?? null,
    delivery: order.pickup
      ? "Pickup at the counter"
      : `Deliver ${order.delivery_date ?? "— date to be confirmed"}`,
    deliveryNote: order.delivery_note ?? null,
    trade: order.trade ?? null,
    lines: items.map((item) => ({
      description: (item.name ?? "").trim() || "—",
      color: item.color,
      qty: Number(item.qty ?? 0),
      unit: item.unit ?? "",
      unitPrice: toCents(item.unit_price),
    })),
    subtotal: documentSubtotal(order),
    // A typed amount is not the rate times anything, so the PDF says "Tax".
    taxRate:
      order.tax_rate == null || taxIsTyped(order)
        ? null
        : Number(order.tax_rate),
    tax: documentTax(order),
    total: documentTotal(order),
    // The credit is asked for on the paperwork as well as in the letter: the
    // email gets read once and the PDF is what reaches their accounts.
    note: credit?.claim ? credit.note : null,
  };
}

/** What the report is called when it lands in the supplier's inbox. */
function reportName(layout: RoofLayout): string {
  return layout.pdf_filename?.trim() || "measurement-report.pdf";
}

/** Print a material order. See `printWorkOrder` — same approach, same header. */
function printMaterialOrder(
  order: MaterialOrder,
  items: NonNullable<MaterialOrder["items"]>,
  supplierName: string | null,
  job: Job | null,
  withPricing: boolean,
): void {
  printOrder({
    title: `Material Order MO-${order.order_number}`,
    header: [
      ...paperworkHeader(job),
      ["Supplier", supplierName],
      ["Trade", order.trade],
      ["Delivery", order.pickup ? "Pickup" : order.delivery_date],
      ["Delivery note", order.delivery_note],
      ["Status", order.status],
    ],
    items: items.map((item) => ({
      description: [item.name, item.color].filter(Boolean).join(" — "),
      qty: item.qty,
      unit: item.unit,
      unitPrice: item.unit_price,
    })),
    total: documentTotal(order),
    tax: {
      subtotal: documentSubtotal(order),
      // A typed amount is not the rate times anything, so no rate is printed.
      rate:
        order.tax_rate == null || taxIsTyped(order)
          ? null
          : Number(order.tax_rate),
      amount: documentTax(order),
    },
    withPricing,
  });
}
